import type { Race, Unit, UnitWeapon } from "@sins2-atlas/game-data";
import { clsx } from "clsx";
import { exactNumber } from "../lib/format";
import type { StatFrame } from "../lib/frame";
import { frameWeaponCells, weaponInTotals } from "../lib/frame-fold";
import { formatStatNumber } from "../lib/stat-cell";
import { researchSubjectName } from "../lib/tech-index";
import { StatCellView } from "./stat-cell";

// The per-battery weapons table and the notes its rows carry — shared by the roster's
// expandable row and the entity page's Weapons card. Moved out of roster-table.tsx whole on
// 2026-09-08 (review: the entity page imported WeaponTable from a module named for the roster,
// and no importer used both halves of that file); nothing inside the moved functions changed.

/** What one weapon battery is, in words a player can read off the row. Item-gated mounts
 * are excluded in BOTH frames (loadouts are outside the stat frames by definition); a
 * research-added mount counts once its subject is in the selection, so its note drops
 * there. Exported for its test. */
export function weaponNotes(weapon: UnitWeapon, frame: StatFrame, race: Race): string[] {
  const notes: string[] = [];
  // The user's own wording (2026-07-29).
  if (weapon.itemGated) notes.push("requires item, not included in stats");
  // Under a Tech Set the gate opens exactly when the subject that adds this mount is
  // ticked, so the note asks the fold rather than reading the frame name (Issue 24). It
  // names the subject and stops (user, 2026-09-08: "requires X research" — the old note's
  // "so Base stats leave it out" was the wordy half); the muted row already shows the
  // battery is outside the totals. The name is the tree's own, never the id.
  if (weapon.researchGated && !weaponInTotals(weapon, frame, race)) {
    const subject =
      weapon.requiredSubjectId === undefined
        ? undefined
        : researchSubjectName(weapon.requiredSubjectId);
    notes.push(subject === undefined ? "requires research" : `requires ${subject} research`);
  }
  if (weapon.weaponType === "planet_bombing") notes.push("planet bombing only");
  // The fact and nothing after it (user, 2026-09-08: no commentary on what the totals do
  // with it); the muted row already says the battery is outside them.
  if (weapon.weaponType === "normal" && weapon.damage === 0) notes.push("deals no damage");
  if ((weapon.burstShots ?? 1) > 1) notes.push(`${weapon.burstShots}-shot burst`);
  if (weapon.firingType === "spawn_torpedo") notes.push("fires torpedoes, which can be shot down");
  return notes;
}

/** A cooldown cell's number with its unit. */
const seconds = (value: number) => `${formatStatNumber(value)}s`;

/** One battery's row. `showPierce` is false in the planet-bombing table, where the column
 * would be Not Applicable on every row — the game omits it there for the same reason. */
function WeaponRow({
  weapon,
  frame,
  unit,
  showPierce,
}: {
  weapon: Unit["weapons"][number];
  frame: StatFrame;
  /** The owning hull: a battery has no Race, shop listing or abilities of its own, and the
   * fold needs all three (frameWeaponCells). */
  unit: Unit;
  showPierce: boolean;
}) {
  const cells = frameWeaponCells(weapon, frame, unit);
  const bombing = weapon.weaponType === "planet_bombing";
  return (
    <tr
      // Dim = excluded from the active frame's unit totals: item-gated always,
      // research-gated whenever the active frame has not unlocked the mount.
      className={clsx(
        "border-t border-line-faint",
        !weaponInTotals(weapon, frame, unit.race) && "text-muted-2",
      )}
    >
      <td className="py-1.5 pr-5 text-ink-2">{weapon.name}</td>
      <td className="py-1.5 pr-5 text-ink-3">{weapon.tags.join(", ")}</td>
      <td className="py-1.5 pr-5 text-right font-mono">×{weapon.count}</td>
      <td className="py-1.5 pr-5 text-right font-mono">
        {/* Every column folds under the frame (2026-09-08): Damage and Cooldown used to show
            the weapon file's figures beside a folded DPS, under a footnote. Population damage
            has no modifier in any file, so it is the one figure read raw. */}
        {bombing && cells.bombingDamage !== undefined ? (
          <>
            <StatCellView cell={cells.bombingDamage} /> / {weapon.populationDamage ?? 0} pop
          </>
        ) : (
          <StatCellView cell={cells.damage} />
        )}
      </td>
      {showPierce && (
        <td className="py-1.5 pr-5 text-right font-mono">
          {weapon.pierce === undefined ? (
            <span
              className="cursor-help text-muted-2 underline decoration-muted decoration-dotted underline-offset-2"
              title="Not Applicable: a planet-bombing weapon cannot target ships. The stat does not exist for this weapon"
            >
              —
            </span>
          ) : (
            exactNumber(weapon.pierce)
          )}
        </td>
      )}
      <td className="py-1.5 pr-5 text-right font-mono">
        <StatCellView cell={cells.cooldown} format={seconds} />
      </td>
      <td className="py-1.5 pr-5 text-right font-mono">
        <StatCellView cell={cells.dps} />
      </td>
      <td className="py-1.5 pr-5 text-right font-mono">
        <StatCellView cell={cells.novaDamage} />
      </td>
      <td className="py-1.5 pr-5 text-right font-mono">
        <StatCellView cell={cells.range} />
      </td>
      <td className="max-w-[300px] whitespace-normal py-1.5 pr-5 text-[12.5px] text-ink-3">
        {bombing ? "planets" : weapon.targets.join(", ")}
      </td>
      <td className="whitespace-normal py-1.5 text-[12.5px] text-muted-2">
        {weaponNotes(weapon, frame, unit.race).join("; ")}
      </td>
    </tr>
  );
}

/** One of the two batteries tables. The game renders normal weapons and planet bombing as
 * separate tables with different columns (ADR 0005), which is also what lets each drop the
 * conditional cells the single mixed table needed. */
function WeaponBatteries({
  weapons,
  frame,
  unit,
  showPierce,
}: {
  weapons: Unit["weapons"];
  frame: StatFrame;
  unit: Unit;
  showPierce: boolean;
}) {
  return (
    <table className="w-fit border-collapse text-[13.5px] [font-variant-numeric:tabular-nums]">
      <thead>
        <tr className="text-left font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-2">
          <th className="py-1 pr-5 font-medium">Weapon</th>
          <th className="py-1 pr-5 font-medium">Type</th>
          <th className="py-1 pr-5 font-medium text-right">Mounts</th>
          <th className="py-1 pr-5 font-medium text-right">Damage</th>
          {showPierce && (
            <th
              className="cursor-help py-1 pr-5 text-right font-medium underline decoration-muted decoration-dotted underline-offset-2"
              title="Pierce reduces the target's Durability, never below zero and never against Armor Strength."
            >
              Pierce
            </th>
          )}
          <th className="py-1 pr-5 font-medium text-right">Cooldown</th>
          <th className="py-1 pr-5 font-medium text-right">DPS</th>
          <th className="py-1 pr-5 font-medium text-right">Nova Damage</th>
          <th className="py-1 pr-5 font-medium text-right">Range</th>
          <th className="py-1 pr-5 font-medium">Targets</th>
          <th className="py-1 font-medium">Notes</th>
        </tr>
      </thead>
      <tbody>
        {weapons.map((weapon) => (
          <WeaponRow
            key={`${weapon.id}|${weapon.itemGated}|${weapon.researchGated ?? false}`}
            weapon={weapon}
            frame={frame}
            unit={unit}
            showPierce={showPierce}
          />
        ))}
      </tbody>
    </table>
  );
}

/** The per-battery weapons table — shared by the roster's expandable row and the entity
 * page's weapons section. */
export function WeaponTable({ unit, frame }: { unit: Unit; frame: StatFrame }) {
  // The game's own grouping: the ship's batteries, then a separate planet-bombing table.
  const normal = unit.weapons.filter((weapon) => weapon.weaponType !== "planet_bombing");
  const bombing = unit.weapons.filter((weapon) => weapon.weaponType === "planet_bombing");
  return (
    <>
      {normal.length > 0 && (
        <WeaponBatteries weapons={normal} frame={frame} unit={unit} showPierce={true} />
      )}
      {bombing.length > 0 && (
        <div className={clsx(normal.length > 0 && "mt-4")}>
          {/* The game labels its second block "Weapons" too, but the columns differ; naming
              it keeps two adjacent tables from reading as one. */}
          <p className="mb-1 font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-2">
            Planet bombing
          </p>
          <WeaponBatteries weapons={bombing} frame={frame} unit={unit} showPierce={false} />
        </div>
      )}
      {unit.weapons.length === 0 && (
        <p className="text-[13.5px] text-ink-3">This hull is unarmed. It has no weapon mounts.</p>
      )}
    </>
  );
}
